import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Droplet,
  Calendar,
  MapPin,
  Send
} from 'lucide-react';
import Navigation from '../components/Navigation';

const GetBaptizedPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    preferredDate: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Baptism signup:', formData);
  };

  const upcomingDates = [
    {
      date: 'August 15, 2025',
      location: 'Main Venue - Pool Area',
      time: '2:00 PM'
    },
    {
      date: 'August 16, 2025',
      location: 'Main Venue - Pool Area',
      time: '11:00 AM'
    },
    {
      date: 'August 17, 2025',
      location: 'Main Venue - Pool Area',
      time: '10:00 AM'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <div className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1438032005730-c779502df39b?w=1920&h=1080&fit=crop')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B]/90 via-[#CFA64B]/20 to-[#0B1B3B]/90" />
        
        <div className="relative z-10 text-center text-white px-4 max-w-5xl">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
            Get <span className="text-[#CFA64B]">Baptized</span>
          </h1>
          <p className="text-xl md:text-2xl mb-10 text-gray-200">
            Take your next step of faith and celebrate new life.
          </p>
          <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold px-10 py-6 text-lg">
            Sign Up for Baptism
          </Button>
        </div>
      </div>

      {/* Section 1: What Baptism Means */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">What Baptism Means</h2>
            <p className="text-xl text-gray-600">A public declaration of your faith and commitment</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Baptism is a beautiful, outward expression of an inward decision to follow Jesus. It represents 
                your old life being washed away and a new life beginning in Christ. It's a moment of celebration, 
                commitment, and community.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                When you're baptized at KNOWN CON, you're not just making a statement — you're joining a movement 
                of believers who have chosen to walk in purpose, faith, and transformation.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Whether you're taking this step for the first time or recommitting your life to Christ, baptism 
                is a powerful declaration that you're ready to live differently.
              </p>
            </div>
            <div className="relative h-96 rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1501612780327-45045538702b?w=800&h=600&fit=crop" 
                alt="Baptism moment" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Section 2: Upcoming Baptism Dates */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Upcoming Baptism Dates</h2>
            <p className="text-xl text-gray-600">Choose a day to make your faith public</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {upcomingDates.map((event, idx) => (
              <Card key={idx} className="border-2 border-gray-200 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 mx-auto mb-6 bg-[#CFA64B]/10 rounded-full flex items-center justify-center">
                    <Droplet className="w-8 h-8 text-[#CFA64B]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0B1B3B] mb-4">{event.date}</h3>
                  <div className="space-y-2 text-gray-600 mb-6">
                    <div className="flex items-center justify-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">{event.location}</span>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">{event.time}</span>
                    </div>
                  </div>
                  <Button className="w-full bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold">
                    Register
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Section 3: Your Next Step */}
      <div className="py-20" style={{ backgroundColor: '#FFF9E9' }}>
        <div className="max-w-[800px] mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Your Next Step</h2>
            <p className="text-xl text-gray-600">Sign up and we'll guide you through the rest</p>
          </div>

          <Card className="border-none shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-[#0B1B3B] mb-2">Preferred Date *</label>
                  <select
                    required
                    value={formData.preferredDate}
                    onChange={(e) => setFormData({...formData, preferredDate: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#CFA64B] focus:outline-none"
                  >
                    <option value="">Select a date</option>
                    <option value="august-15">August 15, 2025 - 2:00 PM</option>
                    <option value="august-16">August 16, 2025 - 11:00 AM</option>
                    <option value="august-17">August 17, 2025 - 10:00 AM</option>
                  </select>
                </div>
                <Button 
                  type="submit"
                  className="w-full bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-bold py-6 text-lg"
                >
                  <Send className="w-5 h-5 mr-2" />
                  Submit Registration
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Section 4: Quote Strip */}
      <div className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B] to-[#CFA64B]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <p className="text-3xl md:text-5xl italic font-light">
            "Faith is stepping forward when you can't see the whole path."
          </p>
        </div>
      </div>

      {/* Quick Links */}
      <div className="py-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="/attendee-guide" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">
              Attendee Guide
            </a>
            <span className="text-gray-400">|</span>
            <a href="/faqs" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">
              FAQs
            </a>
            <span className="text-gray-400">|</span>
            <a href="#contact" className="text-[#0B1B3B] hover:text-[#CFA64B] font-medium transition-colors">
              Contact Support
            </a>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 KNOWN CON. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default GetBaptizedPage;